import { Any, UntrustedApiMethods } from "@cede/types";
import { updateNestedObjectFieldValue } from "@cede/utils";
import { OPTIONAL_MARKER, SELECT_MARKER, StoreMethodMetaData } from "../../constants/methods";
import { WidgetConfig, WidgetConfigMetaData } from "../../constants/widgetConfig";
import { castValue, checkStateValidity, getInitialFormStateForAllMethods } from "./utils";
import { create } from "zustand";
import { persist } from "zustand/middleware";

type FormKey = UntrustedApiMethods | WidgetConfig;

export type ParamsFormsState = {
  persist: boolean;
  togglePersist: () => void;

  mode: "user" | "json";
  toggleMode: () => void;

  forms: Record<FormKey, Record<string, Any>>;
  setForm: (method: FormKey, params: Record<string, Any>) => void;
  setFormValue: (method: StoreMethodMetaData | WidgetConfigMetaData, key: string, value: Any) => void;
  resetForms: () => void;
  prefillValueForAllForms: (key: string, value: Any) => void;
};

export const useParamsForms = create<ParamsFormsState>()(
  persist(
    (set) => ({
      persist: false,
      togglePersist: () => set((state) => ({ persist: !state.persist })),

      mode: "json",
      toggleMode: () => set((state) => ({ mode: state.mode === "user" ? "json" : "user" })),

      forms: getInitialFormStateForAllMethods(),
      setForm: (method, params) => set((state) => ({ forms: { ...state.forms, [method]: params } })),
      setFormValue: (method, key, value) =>
        set((state) => {
          try {
            const newForm = { ...state.forms };
            const type = method.params?.[key] || "string";
            const castedValue = castValue(value, type.includes(SELECT_MARKER) ? "string" : type);
            const apiParamMapping = method.apiParamsMapping?.[key];

            newForm[method.methodName] = updateNestedObjectFieldValue(
              newForm[method.methodName],
              apiParamMapping || key,
              castedValue,
              {
                removeIfUndefined: type.includes(OPTIONAL_MARKER), // remove the field if the value is undefined and the param is optional
              },
            );

            return { forms: newForm };
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
            return state;
          }
        }),
      resetForms: () =>
        set({
          forms: getInitialFormStateForAllMethods(),
        }),
      prefillValueForAllForms: (key, value) =>
        set((state) => {
          const newForms = { ...state.forms };
          const methods = Object.keys(newForms) as UntrustedApiMethods[];

          methods.forEach((methodName) => {
            if (!newForms[methodName]) return;

            if (newForms[methodName][key] === "") {
              newForms[methodName] = { ...newForms[methodName], [key]: value };
            }
          });

          return { forms: newForms };
        }),
    }),
    {
      name: "paramsForms",
      partialize: (state) =>
        state.persist
          ? { forms: state.forms, persist: state.persist, mode: state.mode }
          : { persist: state.persist, mode: state.mode },
      version: Math.random(),
      migrate: (oldState) => {
        const oldParamsFormState = oldState as ParamsFormsState;
        const isStateValid = checkStateValidity(oldParamsFormState);
        return {
          ...oldParamsFormState,
          persist: oldParamsFormState.persist || false,
          mode: oldParamsFormState.mode || "json",
          forms: isStateValid ? oldParamsFormState.forms : getInitialFormStateForAllMethods(),
        };
      },
    },
  ),
);
